/**
 * AP-36 Abschnitt 3 und 7 — die React-Seite des Bausteins:
 *
 * - `useSpeicherstelle(opts)` — die Stelle eines Datensatzes, geteilt über das
 *   Register (7.2); übernimmt neu geladene `daten` (7.6).
 * - `useFeld(stelle, key, opts)` — Props für `<input>`/`<select>`/`<textarea>`,
 *   Speichern beim Verlassen bzw. sofort (3.2), Prüfen vor dem Senden (3.3/3.4),
 *   Esc (3.2), Zustand für `FeldStatus` (3.5).
 * - `useGruppe` / `<Feldgruppe>` — Felder, die nur zusammen gültig sind, speichern
 *   beim Verlassen der **Gruppe** (3.1). `ZeilenBlock` nutzt dieselbe Logik.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { FeldStatus } from './anzeige'
import { fuegeZusammen, pfadPatch } from './pfad'
import { speicherRegister } from './register'
import type { Datensatz, Ergebnis, Speicherstelle, StelleOptionen } from './stelle'
import { alsText, gleich } from './vergleich'
import { leseZahl, zahlText } from './zahl'

// ── Speicherstelle ────────────────────────────────────────────────────────────

/**
 * Die Stelle zum Datensatz. `daten` ist der geladene Datensatz (GET-Form); ein
 * neuerer Stand wird übernommen, ein älterer nicht (7.2, 7.6).
 */
export function useSpeicherstelle<A = unknown>(opts: StelleOptionen<A> & { daten?: A | null }): Speicherstelle<A> {
  const { daten, ...optionen } = opts
  // Holen ohne Zählen im Render (StrictMode ruft Effekte doppelt) — gezählt wird im Effekt.
  const stelle = speicherRegister.holeOhneZaehlen(optionen)
  stelle.setzeOptionen(optionen)
  // Eine frische Stelle bekommt ihren Datensatz schon hier, bevor das erste Feld sie liest. Erst im
  // Layout-Effekt wäre es einen Durchgang zu spät: `useSyncExternalStore` abonniert in einem passiven
  // Effekt, jedes Feld käme also leer ins DOM, und sein Wert folgte erst nach dem Zeichnen des Browsers.
  if (daten) stelle.beginne(daten)

  useEffect(() => {
    speicherRegister.melde(stelle as Speicherstelle<unknown>)
    return () => speicherRegister.gib(stelle as Speicherstelle<unknown>)
  }, [stelle])

  useLayoutEffect(() => {
    if (daten) stelle.uebernimm(daten)
  }, [stelle, daten])

  return stelle
}

/** Rendert neu, wenn sich Stand oder Basis der Stelle ändern. */
function useStellenVersion(stelle: Speicherstelle<unknown>): number {
  return useSyncExternalStore(
    useCallback((fn) => stelle.abonniere(fn), [stelle]),
    () => stelle.aenderungsZaehler,
    () => stelle.aenderungsZaehler,
  )
}

/**
 * Die Basis der Stelle zum Anzeigen — für Masken, die eine Liste aus der Stelle
 * zeichnen (Zusatzfelder, Regeln), statt jedes Feld über `useFeld` zu binden.
 */
export function useStellenDaten(stelle: Speicherstelle<unknown>): Datensatz {
  useStellenVersion(stelle)
  return stelle.daten
}

// ── Zustand und Anzeige ───────────────────────────────────────────────────────

export type Zustand = 'ruhig' | 'speichert' | 'gespeichert' | 'fehler'

export interface Status {
  zustand: Zustand
  /** Server- oder Prüffehler; bei Fehler bleibt die Eingabe stehen. */
  fehler: string | null
  /** Nur bei Serverfehlern: noch einmal senden. */
  erneut?: () => void
}

/** 3.5: Häkchen 1,5 s, dann ruhig. */
function useZustand() {
  const [zustand, setZustandRoh] = useState<Zustand>('ruhig')
  const zeit = useRef<ReturnType<typeof setTimeout>>()
  const setZustand = useCallback((z: Zustand) => {
    clearTimeout(zeit.current)
    setZustandRoh(z)
    if (z === 'gespeichert') zeit.current = setTimeout(() => setZustandRoh('ruhig'), 1500)
  }, [])
  useEffect(() => () => clearTimeout(zeit.current), [])
  return [zustand, setZustand] as const
}

// ── Gruppe ────────────────────────────────────────────────────────────────────

/** Was ein Feld seiner Gruppe zeigt. */
interface Mitglied {
  key: string
  label?: string
  /** Ungesendete, geänderte Eingabe? */
  geaendert(): boolean
  /** Prüft und setzt den eigenen Prüffehler; `null` = gültig. */
  pruefe(): string | null
  /** Patch dieses Felds samt Folgefeldern (nur wenn geändert und gültig). */
  patch(): Datensatz
  /** Die Felder dieser Änderung (Pfade bleiben Pfade) — für den Konfliktvergleich. */
  felder(): string[]
  /** Aktueller Wert (Eingabe oder Basis) — für die Prüfung über die Gruppe. */
  wert(): unknown
  /** 7.3: Hat sich die Basis geändert, während der Benutzer tippte? Dann fragen. */
  frageBeiFremderAenderung(): Promise<boolean>
  nachErgebnis(e: Ergebnis): void
  zuruecksetzen(): void
}

interface GruppenKontextWert {
  stelle: Speicherstelle<unknown>
  melde(m: Mitglied): () => void
  /** Ein Mitglied hat eine Auswahl getroffen oder Enter gedrückt — die Gruppe speichert jetzt. */
  jetzt(): void
}

const GruppenKontext = createContext<GruppenKontextWert | null>(null)

export interface GruppenOptionen {
  /** Prüfung über die Gruppe, z. B. Bis nicht vor Von. Bekommt Eingabe bzw. Basis je Feld. */
  pruefe?: (werte: Datensatz) => string | null
  /** Für Konfliktfrage und Hinweis. */
  label?: string
  /** Die Gruppe schreibt nicht selbst (Zeilenblock mit eigenem `onVerlassen`). */
  gesperrt?: boolean
}

export interface Gruppe extends Status {
  kontext: GruppenKontextWert
  /** Den Fokus hat die Gruppe verlassen: prüfen und senden. */
  verlassen(): Promise<Ergebnis | null>
  /** Esc bzw. „Verwerfen": alle Mitglieder zurück auf die Basis. */
  verwerfen(): void
  /** Hat ein Mitglied eine ungesendete Änderung? */
  geaendert(): boolean
  onFocus: () => void
  onBlur: (e: FocusEvent<HTMLElement>) => void
}

/**
 * Logik einer Gruppe — von `<Feldgruppe>` und `ZeilenBlock` gleichermaßen
 * benutzt. Sie speichert **einen** Auftrag mit allen geänderten Feldern.
 */
export function useGruppe(stelle: Speicherstelle<unknown> | null | undefined, opts: GruppenOptionen = {}): Gruppe {
  const id = useId()
  const mitglieder = useRef(new Set<Mitglied>())
  const [zustand, setZustand] = useZustand()
  const [fehler, setFehler] = useState<string | null>(null)
  const optsRef = useRef(opts)
  optsRef.current = opts
  const letzterPatch = useRef<Datensatz | null>(null)
  const letzteFelder = useRef<string[]>([])

  const sende = useCallback(
    async (patch: Datensatz, felder: string[], labels: Record<string, string>): Promise<Ergebnis | null> => {
      if (!stelle) return null
      letzterPatch.current = patch
      letzteFelder.current = felder
      setZustand('speichert')
      setFehler(null)
      stelle.ungesendet.delete(id)
      const ergebnis = await stelle.sende({ kennung: id, felder, labels, patch })
      for (const m of mitglieder.current) if (felder.includes(m.key)) m.nachErgebnis(ergebnis)
      if (ergebnis.art === 'gespeichert') setZustand('gespeichert')
      else if (ergebnis.art === 'fehler') {
        setZustand('fehler')
        setFehler(ergebnis.text)
      } else if (ergebnis.art === 'verworfen') setZustand('ruhig')
      return ergebnis
    },
    [stelle, id, setZustand],
  )

  /** Läuft ein Verlassen noch (Rückfrage, Senden), bekommt ein zweites dasselbe Versprechen. */
  const inArbeit = useRef<Promise<Ergebnis | null> | null>(null)

  const verlassenEinmal = useCallback(async (): Promise<Ergebnis | null> => {
    if (!stelle || optsRef.current.gesperrt) return null
    const geaenderte = [...mitglieder.current].filter((m) => m.geaendert())
    if (geaenderte.length === 0) return null
    // Jedes Feld prüft sich selbst, dann die Gruppe als Ganzes (3.3).
    const feldFehler = geaenderte.map((m) => m.pruefe()).filter(Boolean)
    const werte: Datensatz = {}
    for (const m of mitglieder.current) werte[m.key] = m.wert()
    const gruppenFehler = feldFehler.length ? null : (optsRef.current.pruefe?.(werte) ?? null)
    if (feldFehler.length || gruppenFehler) {
      stelle.pruefFehler.add(id)
      // Feldfehler zeigt das Feld selbst; am Gruppenrand steht nur, was die Gruppe als Ganzes betrifft.
      setZustand(gruppenFehler ? 'fehler' : 'ruhig')
      setFehler(gruppenFehler)
      return null
    }
    stelle.pruefFehler.delete(id)
    for (const m of geaenderte) {
      if (!(await m.frageBeiFremderAenderung())) {
        for (const x of geaenderte) x.zuruecksetzen()
        setZustand('ruhig')
        return { art: 'verworfen' }
      }
    }
    let patch: Datensatz = {}
    const felder: string[] = []
    const labels: Record<string, string> = {}
    for (const m of geaenderte) {
      patch = fuegeZusammen(patch, m.patch())
      felder.push(...m.felder())
      if (m.label) labels[m.key] = m.label
    }
    return sende(patch, felder, labels)
  }, [stelle, id, sende, setZustand])

  const verlassen = useCallback((): Promise<Ergebnis | null> => {
    if (inArbeit.current) return inArbeit.current
    const p = verlassenEinmal().finally(() => {
      inArbeit.current = null
    })
    inArbeit.current = p
    return p
  }, [verlassenEinmal])

  const verwerfen = useCallback(() => {
    for (const m of mitglieder.current) m.zuruecksetzen()
    stelle?.pruefFehler.delete(id)
    stelle?.ungesendet.delete(id)
    setFehler(null)
    setZustand('ruhig')
  }, [stelle, id, setZustand])

  const kontext = useMemo<GruppenKontextWert | null>(
    () =>
      stelle
        ? {
            stelle,
            melde: (m) => {
              mitglieder.current.add(m)
              return () => mitglieder.current.delete(m)
            },
            jetzt: () => void verlassen(),
          }
        : null,
    [stelle, verlassen],
  )

  // Listen-Gruppen gehen beim Schließen nicht über keepalive (S-21) — der Browser warnt.
  const markiereUngesendet = useCallback(() => {
    if (!stelle) return
    if ([...mitglieder.current].some((m) => m.geaendert())) stelle.ungesendet.set(id, { patch: () => null, keepalive: false })
    else stelle.ungesendet.delete(id)
  }, [stelle, id])

  useEffect(
    () => () => {
      stelle?.ungesendet.delete(id)
      stelle?.pruefFehler.delete(id)
    },
    [stelle, id],
  )

  return {
    kontext: kontext as GruppenKontextWert,
    zustand,
    fehler,
    erneut:
      zustand === 'fehler' && letzterPatch.current && stelle
        ? () => void sende(letzterPatch.current!, letzteFelder.current, {})
        : undefined,
    verlassen,
    verwerfen,
    geaendert: () => [...mitglieder.current].some((m) => m.geaendert()),
    onFocus: markiereUngesendet,
    onBlur: (e) => {
      markiereUngesendet()
      if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
      void verlassen()
    },
  }
}

/**
 * Felder, die nur zusammen gültig sind (Von/Bis, PLZ/Ort …). Speichert, wenn der
 * Fokus die Gruppe verlässt — nicht beim Wechsel zwischen ihren Feldern.
 */
export function Feldgruppe({
  stelle,
  pruefe,
  label,
  className,
  children,
  status,
}: {
  stelle: Speicherstelle<unknown>
  pruefe?: GruppenOptionen['pruefe']
  label?: string
  className?: string
  children: ReactNode
  /** Anzeige des Zustands am Gruppenrand (Vorgabe: `FeldStatus` unter der Gruppe, 3.5). */
  status?: (s: Status) => ReactNode
}) {
  const gruppe = useGruppe(stelle, { pruefe, label })
  return (
    <GruppenKontext.Provider value={gruppe.kontext}>
      <div className={className} onFocus={gruppe.onFocus} onBlur={gruppe.onBlur} data-feldgruppe>
        {children}
        {status ? status(gruppe) : <FeldStatus status={gruppe} />}
      </div>
    </GruppenKontext.Provider>
  )
}

/** Für `ZeilenBlock`: dieselbe Gruppe als Kontext bereitstellen. */
export function GruppenBereich({ gruppe, children }: { gruppe?: Gruppe; children: ReactNode }) {
  if (!gruppe?.kontext) return <>{children}</>
  return <GruppenKontext.Provider value={gruppe.kontext}>{children}</GruppenKontext.Provider>
}

// ── Feld ──────────────────────────────────────────────────────────────────────

export type FeldArt = 'text' | 'zahl' | 'datum' | 'auswahl' | 'schalter'

export interface FeldOptionen<T> {
  label?: string
  /** Standard `text`. `auswahl` und `schalter` speichern sofort (3.2). */
  art?: FeldArt
  /** Text → Wert. Vorgabe je Art (leer → `null`). */
  lies?: (text: string) => T
  /** Wert → Text. Vorgabe je Art. */
  schreib?: (wert: T) => string
  pflicht?: boolean
  pruefe?: (wert: T) => string | null
  /** Folgefelder, die mit demselben PATCH gehen (3.1). */
  folge?: (wert: T) => Datensatz
  gesperrt?: boolean
  /** Nachkommastellen für `zahl`. */
  stellen?: number
  /**
   * Die Gruppe, zu der das Feld gehört, wenn der Hook **außerhalb** ihres
   * Kontexts aufgerufen wird — typisch in einer Zeile, die `ZeilenBlock` erst
   * rendert. Sonst gilt die umgebende `<Feldgruppe>`.
   */
  gruppe?: Gruppe
}

export interface Feld<T> extends Status {
  /** Für `<input>`, `<select>`, `<textarea>`. */
  props: {
    name: string
    value: string
    disabled?: boolean
    'aria-invalid'?: boolean
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
    onBlur: () => void
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => void
    ref: (el: HTMLElement | null) => void
  }
  /** Für Kontrollkästchen und Schalter. */
  schalter: {
    name: string
    checked: boolean
    disabled?: boolean
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
    ref: (el: HTMLElement | null) => void
  }
  /** Aktueller Wert (Eingabe, sonst gespeichert). */
  wert: T
  /** Gespeicherter Wert. */
  gespeichert: T
  /** Wert setzen und sofort speichern (Auswahl aus einer Suche). */
  setze: (wert: T) => void
  /**
   * Nur die Eingabe setzen, ohne zu speichern — für eigene Eingabe-Bausteine,
   * die kein `<input>` durchreichen (Vorschlagsfeld, Maske). Gespeichert wird
   * wie sonst beim Verlassen bzw. über `setze`.
   */
  tippe: (text: string) => void
  label?: string
}

/**
 * Trägt eines der Felder eine Eingabe, die vom gespeicherten Wert abweicht?
 *
 * Für **abgeleitete Anzeigen** einer Zeile (Gesamtpreis aus Menge, Einzelpreis, Preiseinheit, Rabatt): Solange
 * eine Eingabe offen ist, rechnet die Maske mit dem, was dasteht (`feld.wert`) — nicht erst nach dem Speichern
 * der Zeile. Ohne offene Eingabe gilt der Wert des Servers.
 */
export function eingabeOffen(...felder: Pick<Feld<unknown>, 'wert' | 'gespeichert'>[]): boolean {
  return felder.some((f) => !gleich(f.wert, f.gespeichert))
}

const TAG = /^\d{4}-\d{2}-\d{2}$/

function vorgabeLies<T>(art: FeldArt): (text: string) => T {
  switch (art) {
    case 'zahl':
      return (t) => leseZahl(t) as T
    case 'schalter':
      return (t) => (t === 'true') as T
    default:
      return (t) => (t.trim() === '' ? null : t) as T
  }
}

function vorgabeSchreib<T>(art: FeldArt, stellen?: number): (wert: T) => string {
  switch (art) {
    case 'zahl':
      return (w) => (typeof w === 'number' ? zahlText(w, stellen) : w == null ? '' : zahlText(Number(w), stellen))
    case 'datum':
      return (w) => (w == null ? '' : String(w).slice(0, 10))
    case 'schalter':
      return (w) => String(Boolean(w))
    default:
      return (w) => (w == null ? '' : String(w))
  }
}

/** Grund, warum eine Eingabe nicht gesendet wird — oder `null`. */
function pruefeEingabe<T>(art: FeldArt, text: string, wert: T, opts: FeldOptionen<T>): string | null {
  const leer = wert === null || wert === undefined || (typeof wert === 'string' && wert.trim() === '')
  if (opts.pflicht && leer) return 'Pflichtfeld'
  if (art === 'zahl' && text.trim() !== '' && wert === null) return 'Keine gültige Zahl'
  if (art === 'datum' && !leer) {
    // 3.4: nur ein vollständiges, gültiges Datum geht hinaus (Stückliste friert das erste ein).
    const t = String(wert)
    const jahr = Number(t.slice(0, 4))
    if (!TAG.test(t) || Number.isNaN(Date.parse(`${t}T00:00:00Z`)) || jahr < 1900) return 'Datum unvollständig'
  }
  return leer ? null : (opts.pruefe?.(wert) ?? null)
}

export function useFeld<T = string | null>(
  stelle: Speicherstelle<unknown>,
  key: string,
  opts: FeldOptionen<T> = {},
): Feld<T> {
  useStellenVersion(stelle)
  const umgebend = useContext(GruppenKontext)
  const gruppe = opts.gruppe?.kontext ?? umgebend
  const inGruppe = gruppe !== null && gruppe.stelle === stelle
  const art = opts.art ?? 'text'
  const lies = opts.lies ?? vorgabeLies<T>(art)
  const schreib = opts.schreib ?? vorgabeSchreib<T>(art, opts.stellen)

  const gespeichert = stelle.basis(key) as T
  const [eingabe, setEingabe] = useState<string | null>(null)
  const [zustand, setZustand] = useZustand()
  const [fehler, setFehler] = useState<string | null>(null)
  const element = useRef<HTMLElement | null>(null)
  const gesendet = useRef<string | null>(null)
  /** Basis, als der Benutzer zu tippen begann (7.3 „Übernehmen" bei offener Eingabe). */
  const basisBeiEingabe = useRef<unknown>(undefined)

  const text = eingabe ?? schreib(gespeichert)
  const wert = (eingabe === null ? gespeichert : lies(eingabe)) as T

  // Aktuelle Werte für die Rückrufe außerhalb des Renders.
  const aktuell = useRef({ eingabe, gespeichert, opts, lies, schreib, art })
  aktuell.current = { eingabe, gespeichert, opts, lies, schreib, art }

  /** Text, der gerade unterwegs ist — ein zweites Verlassen sendet ihn nicht noch einmal. */
  const unterwegs = useRef<string | null>(null)

  const geaendert = useCallback(() => {
    const a = aktuell.current
    return a.eingabe !== null && a.eingabe !== unterwegs.current && !gleich(a.lies(a.eingabe), a.gespeichert)
  }, [])

  const patchVon = useCallback(
    (w: T): Datensatz => fuegeZusammen(pfadPatch(key, w), aktuell.current.opts.folge?.(w) ?? {}),
    [key],
  )
  /** Die Felder der eigenen Änderung für den Konfliktvergleich — Pfade bleiben Pfade. */
  const felderVon = useCallback(
    (w: T): string[] => [key, ...Object.keys(aktuell.current.opts.folge?.(w) ?? {})],
    [key],
  )

  /**
   * 7.3: Wurde die Basis übernommen, während eine Eingabe offen war, und weicht
   * sie ab, fragt das Feld vor dem Senden — ein Stand-Konflikt käme hier nicht
   * mehr, weil die Stelle den neuen Stand schon kennt.
   */
  const frageBeiFremderAenderung = useCallback(async (): Promise<boolean> => {
    const a = aktuell.current
    if (gleich(basisBeiEingabe.current, a.gespeichert)) return true
    const label = a.opts.label ?? key
    const eigen = a.eingabe === null ? null : a.lies(a.eingabe)
    const ja = await speicherRegister.frage(
      `„${label}" wurde inzwischen auf „${alsText(a.gespeichert)}" geändert. Deinen Wert „${alsText(eigen)}" speichern?`,
      'Meinen Wert speichern',
    )
    if (ja) basisBeiEingabe.current = a.gespeichert
    return ja
  }, [key])

  const zuruecksetzen = useCallback(() => {
    setEingabe(null)
    setFehler(null)
    setZustand('ruhig')
    stelle.pruefFehler.delete(key)
    stelle.ungesendet.delete(key)
    stelle.serverFehler.delete(key)
  }, [stelle, key, setZustand])

  const nachErgebnis = useCallback(
    (e: Ergebnis) => {
      if (e.art === 'ersetzt') return
      unterwegs.current = null
      if (e.art === 'fehler') {
        setZustand('fehler')
        setFehler(e.text)
        return
      }
      // Tippt der Benutzer weiter, während gespeichert wurde, bleibt seine neue
      // Eingabe — und ihre Basis ist jetzt der eigene gespeicherte Wert, kein fremder.
      basisBeiEingabe.current = stelle.basis(key)
      setEingabe((jetzt) => (jetzt === gesendet.current ? null : jetzt))
      setFehler(null)
      setZustand(e.art === 'gespeichert' ? 'gespeichert' : 'ruhig')
    },
    [stelle, key, setZustand],
  )

  /** Prüfen und senden — für Felder außerhalb einer Gruppe. */
  const speichere = useCallback(
    async (textJetzt?: string) => {
      const a = aktuell.current
      const t = textJetzt ?? a.eingabe
      if (t === null || a.opts.gesperrt || t === unterwegs.current) return
      const w = a.lies(t)
      if (gleich(w, a.gespeichert)) {
        zuruecksetzen()
        return
      }
      const grund = pruefeEingabe(a.art, t, w, a.opts)
      if (grund) {
        stelle.pruefFehler.add(key)
        setFehler(grund)
        setZustand('fehler')
        return
      }
      stelle.pruefFehler.delete(key)
      // Schon vor der Rückfrage als unterwegs markieren — ein zweites Verlassen
      // (Blur nach Enter) käme sonst hier noch einmal durch.
      unterwegs.current = t
      if (!(await frageBeiFremderAenderung())) {
        unterwegs.current = null
        zuruecksetzen()
        return
      }
      stelle.ungesendet.delete(key)
      gesendet.current = t
      setFehler(null)
      setZustand('speichert')
      const patch = patchVon(w)
      const labels = a.opts.label ? { [key]: a.opts.label } : undefined
      nachErgebnis(await stelle.sende({ kennung: key, felder: felderVon(w), labels, patch }))
    },
    [stelle, key, patchVon, felderVon, nachErgebnis, zuruecksetzen, setZustand, frageBeiFremderAenderung],
  )

  // In der Gruppe: melden, was die Gruppe zum Speichern braucht.
  useEffect(() => {
    if (!inGruppe) return
    return gruppe!.melde({
      key,
      label: opts.label,
      geaendert,
      pruefe: () => {
        const a = aktuell.current
        if (a.eingabe === null) return null
        const grund = pruefeEingabe(a.art, a.eingabe, a.lies(a.eingabe), a.opts)
        setFehler(grund)
        return grund
      },
      patch: () => {
        const a = aktuell.current
        gesendet.current = a.eingabe
        unterwegs.current = a.eingabe
        return a.eingabe === null ? {} : patchVon(a.lies(a.eingabe))
      },
      felder: () => {
        const a = aktuell.current
        return a.eingabe === null ? [] : felderVon(a.lies(a.eingabe))
      },
      wert: () => {
        const a = aktuell.current
        return a.eingabe === null ? a.gespeichert : a.lies(a.eingabe)
      },
      frageBeiFremderAenderung,
      nachErgebnis: (e) => {
        if (e.art === 'ersetzt') return
        unterwegs.current = null
        if (e.art === 'fehler') return // die Gruppe zeigt ihn am Rand
        basisBeiEingabe.current = stelle.basis(key)
        setEingabe((jetzt) => (jetzt === gesendet.current ? null : jetzt))
        setFehler(null)
      },
      zuruecksetzen: () => {
        setEingabe(null)
        setFehler(null)
      },
    })
  }, [inGruppe, gruppe, stelle, key, opts.label, geaendert, patchVon, felderVon, frageBeiFremderAenderung])

  // 7.4: gültige, ungesendete Eingabe → beim Schließen per keepalive.
  useEffect(() => {
    if (inGruppe || eingabe === null) return
    stelle.ungesendet.set(key, {
      keepalive: true,
      patch: () => {
        const a = aktuell.current
        if (a.eingabe === null) return null
        const w = a.lies(a.eingabe)
        return pruefeEingabe(a.art, a.eingabe, w, a.opts) || gleich(w, a.gespeichert) ? null : patchVon(w)
      },
    })
  }, [stelle, key, eingabe, inGruppe, patchVon])

  // Esc gehört zuerst dem Feld (S-18), über das Register vor dem `Modal`.
  const escAbmelden = useRef<(() => void) | null>(null)
  useEffect(() => {
    const el = element.current
    escAbmelden.current?.()
    escAbmelden.current = null
    if (!el || (eingabe === null && !fehler)) return
    escAbmelden.current = speicherRegister.meldeEsc(el, () => {
      zuruecksetzen()
      return true
    })
    return () => {
      escAbmelden.current?.()
      escAbmelden.current = null
    }
  }, [eingabe, fehler, zuruecksetzen])

  // Wird die Maske abgebaut, während ein Feld eine gültige Änderung trägt (Tastenkürzel
  // ohne Blur), geht sie noch hinaus — die Stelle lebt im Register weiter (7.4).
  useEffect(
    () => () => {
      if (inGruppe) return
      stelle.ungesendet.delete(key)
      stelle.pruefFehler.delete(key)
      const a = aktuell.current
      if (a.eingabe === null) return
      // Was schon unterwegs oder eben gespeichert ist, geht nicht noch einmal
      // hinaus: Speichern lässt manche Maske neu aufbauen (die Profilseite lädt
      // den Benutzer neu), und der Abbau sähe die Eingabe sonst als offen an.
      if (a.eingabe === unterwegs.current || a.eingabe === gesendet.current) return
      const w = a.lies(a.eingabe)
      // Gegen die **aktuelle** Basis, nicht gegen den letzten Render dieses
      // Felds — die Antwort kann sie inzwischen weitergestellt haben.
      if (pruefeEingabe(a.art, a.eingabe, w, a.opts) || gleich(w, stelle.basis(key))) return
      void stelle.sende({ kennung: key, felder: felderVon(w), patch: patchVon(w), labels: a.opts.label ? { [key]: a.opts.label } : undefined })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stelle, key],
  )

  const sofort = art === 'auswahl' || art === 'schalter'
  const ref = useCallback((el: HTMLElement | null) => {
    element.current = el
  }, [])

  const aendere = (neu: string) => {
    if (eingabe === null) basisBeiEingabe.current = gespeichert
    setEingabe(neu)
    // Die Gruppe liest ihre Mitglieder über `aktuell` — ohne das sähe `jetzt()`
    // den alten Stand dieses Felds, denn der neue Render ist noch nicht gelaufen.
    aktuell.current = { ...aktuell.current, eingabe: neu }
    if (zustand === 'fehler' && fehler) setFehler(null)
    if (!sofort) return
    if (inGruppe) gruppe!.jetzt()
    else void speichere(neu)
  }

  return {
    zustand: inGruppe ? (fehler ? 'fehler' : 'ruhig') : zustand,
    fehler,
    erneut: zustand === 'fehler' && !inGruppe && eingabe !== null && stelle.serverFehler.has(key) ? () => void speichere() : undefined,
    wert,
    gespeichert,
    label: opts.label,
    tippe: aendere,
    // `setze` ist ein ausdrückliches Setzen von außen (Auswahl aus einer Suche,
    // Farbwähler, Vorgabe übernehmen) — es speichert immer, auch bei Text.
    setze: (w: T) => {
      const neu = schreib(w)
      aendere(neu)
      if (!sofort) {
        if (inGruppe) gruppe!.jetzt()
        else void speichere(neu)
      }
    },
    props: {
      name: key,
      value: text,
      disabled: opts.gesperrt || undefined,
      'aria-invalid': fehler ? true : undefined,
      // Zahlenfelder nehmen nur, was eine Zahl werden kann (AP-37 N1-30): Ziffern, Komma, Punkt, Vorzeichen.
      onChange: (e) => aendere(art === 'zahl' ? e.target.value.replace(/[^0-9,.\-+\s]/g, '') : e.target.value),
      onBlur: () => {
        if (!inGruppe && !sofort) void speichere()
      },
      onKeyDown: (e) => {
        const ziel = e.target as HTMLElement
        if (e.key === 'Enter' && ziel.tagName === 'INPUT' && !(e.nativeEvent as globalThis.KeyboardEvent).isComposing) {
          // 3.2: Enter speichert wie Verlassen, der Fokus bleibt (im Zeilenblock: nächste Zeile).
          if (inGruppe) return
          e.preventDefault()
          void speichere()
        }
      },
      ref,
    },
    schalter: {
      name: key,
      checked: eingabe !== null ? eingabe === 'true' : Boolean(gespeichert),
      disabled: opts.gesperrt || undefined,
      onChange: (e) => aendere(String(e.target.checked)),
      ref,
    },
  }
}
